import React, { useState, useEffect } from 'react';
import LiveMonitoringDemo from './LiveMonitoringDemo';
import ABTestManager from './ABTestManager';
import CampaignGenerationDemo from './CampaignGenerationDemo';
import { NavigationProvider, useNavigation } from './NavigationBridge';
import { NavigationHeader } from './NavigationHeader';
import { ContextPreservationProvider } from './ContextPreservation';

interface ABTestNavigationProps {
  onClose?: () => void;
}

// Internal component that uses navigation context
const ABTestNavigationContent: React.FC<ABTestNavigationProps> = ({ onClose }) => {
  const { navigateTo, addToRecent } = useNavigation();
  const [activeView, setActiveView] = useState<'live-monitor' | 'test-manager' | 'campaign-generation'>('live-monitor');

  // Update navigation context when view changes
  useEffect(() => {
    navigateTo(activeView, { view: activeView });
    // Don't automatically add breadcrumbs for demo navigation to prevent tab accumulation
  }, [activeView, navigateTo]);

  const navigationItems = [
    {
      id: 'live-monitor',
      label: 'Live Monitoring',
      icon: '🔴',
      description: 'Real-time monitoring and automated winner detection'
    },
    {
      id: 'test-manager',
      label: 'Test Manager',
      icon: '⚙️',
      description: 'Create and configure new A/B tests'
    },
    {
      id: 'campaign-generation',
      label: 'AI Campaign Generation',
      icon: '🚀',
      description: 'AI-driven campaign generation from A/B test winners'
    }
  ];

  const handleCreateTest = () => {
    addToRecent({
      type: 'test',
      name: 'New A/B Test',
      path: 'test-manager',
      context: { action: 'create' }
    });
    setActiveView('test-manager');
  };

  // Removed unused handlers - handleEditTemplate and handleViewTestDetails

  const handleMarkTestableElement = (blockId: string, testType: any) => {
    console.log('Mark testable element:', blockId, testType);
  };

  const handleTransitionToABTestView = (testId: string, context: any) => {
    addToRecent({
      type: 'test',
      name: `Test ${testId}`,
      path: 'dashboard',
      context: { testId, ...context }
    });
    navigateTo('dashboard', { testId, ...context });
  };

  const getCurrentItem = () => {
    const currentNavItem = navigationItems.find(item => item.id === activeView);
    if (currentNavItem) {
      return {
        type: 'test' as const,
        name: currentNavItem.label,
        path: activeView,
        context: { view: activeView }
      };
    }
    return undefined;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-700">
      {/* Enhanced Navigation Header */}
      <NavigationHeader
        title="A/B Testing Suite"
        subtitle={navigationItems.find(item => item.id === activeView)?.description}
        currentItem={getCurrentItem()}
        onCreateTest={handleCreateTest}
        showCreateButton={true}
        actions={
          <div className="flex items-center space-x-3">
            {onClose && (
              <button
                onClick={onClose}
                className="text-content-subtle hover:text-content-secondary transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-700 dark:text-green-300">Automated System Active</span>
            </div>
          </div>
        }
      />

      {/* Multi-tab Support - Disabled to prevent tab accumulation */}
      {/* <TabBar /> */}

      {/* Navigation Tabs */}
      <div className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 py-4">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeView === item.id
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50'
                  : 'text-content-secondary hover:text-content hover:bg-gray-100 dark:hover:bg-dark-700'
                  }`}
                title={item.description}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
                {item.id === 'live-monitor' && (
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1">
        {activeView === 'live-monitor' && (
          <LiveMonitoringDemo />
        )}

        {activeView === 'test-manager' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <ABTestManager
              template={{
                id: 'demo-template',
                name: 'Demo Template',
                category: 'promotional',
                content: [
                  {
                    id: 'demo-button-1',
                    type: 'button',
                    content: { text: 'Get Started Now' },
                    styles: {},
                    responsive: { desktop: {}, tablet: {}, mobile: {} },
                    position: { order: 1 },
                    metadata: { locked: false, hidden: false, name: 'CTA Button' }
                  }
                ]
              } as any}
              selectedBlockId={null}
              onCreateTest={handleCreateTest}
              onMarkTestableElement={handleMarkTestableElement}
              onTransitionToABTestView={handleTransitionToABTestView}
              isVisible={true}
              onClose={() => setActiveView('live-monitor')}
            />
          </div>
        )}

        {activeView === 'campaign-generation' && (
          <CampaignGenerationDemo />
        )}
      </div>
    </div>
  );
};

// Main component with NavigationProvider wrapper
const ABTestNavigation: React.FC<ABTestNavigationProps> = ({ onClose }) => {
  return (
    <NavigationProvider>
      <ContextPreservationProvider>
        <ABTestNavigationContent onClose={onClose} />
      </ContextPreservationProvider>
    </NavigationProvider>
  );
};

export default ABTestNavigation;